import { useState } from 'react'
import { Box, Button, Flex, Heading, Stack, Text } from '@chakra-ui/react'
import { Navigate, useParams } from 'react-router-dom'
import { LuPencilLine } from 'react-icons/lu'
import { sectionStateFor, useEventWorkspace } from '../contexts/EventWorkspaceContext'
import { canEditSection, findSection, isSectionVisible, visibleGroups } from '../components/eventSettings/sections'
import { toaster } from '../components/ui/toaster'
import { useTranslation } from '../i18n'

/**
 * `/events/:eventId/settings/:section` — one settings tab. A 'draft' tab is
 * read-only until its Edit; then its fields open, and Save writes only what
 * this tab changed. One tab is edited at a time.
 */
export default function EventSettingsPage() {
  const { section: sectionId } = useParams()
  const ws = useEventWorkspace()
  const { t } = useTranslation()
  const [saving, setSaving] = useState(false)

  const s = sectionStateFor(ws, sectionId)
  const section = findSection(sectionId)
  if (!section || !isSectionVisible(section, s)) {
    // An unknown or hidden tab (a stale link, a type that hides it) lands on
    // the first tab this person gets.
    const first = visibleGroups(s)[0]?.sections[0]
    return first ? <Navigate to={`/events/${ws.eventId}/settings/${first.id}`} replace /> : null
  }

  const editable = canEditSection(section, s)
  const editing = ws.editingSection === section.id
  // Another tab holds unsaved changes: this one waits until they are settled.
  const blocked = ws.unsaved && !editing

  const handleSave = async () => {
    setSaving(true)
    try {
      await ws.saveEdit()
      toaster.create({ title: t('events.eventUpdated'), status: 'success' })
    } catch (err) {
      toaster.create({ title: t('events.eventUpdateFailed'), description: err.message, status: 'error' })
    } finally {
      setSaving(false)
    }
  }

  const { Component } = section

  return (
    <Stack gap={{ base: 4, lg: 6 }}>
      <Flex gap={4} align="flex-start" justify="space-between" wrap="wrap">
        {/* On a phone the section picker above already names the tab. */}
        <Box maxW="65ch" srOnly={{ base: true, lg: false }}>
          <Heading as="h1" size="xl" lineHeight="1.2">{t(section.labelKey)}</Heading>
          {section.descKey && <Text mt={1} color="fg.muted">{t(section.descKey)}</Text>}
        </Box>

        {editable && (
          <Flex gap={3} flexShrink={0}>
            {editing ? (
              <>
                <Button size="sm" variant="outline" disabled={saving} onClick={ws.discardEdit}>
                  {t('common.cancel')}
                </Button>
                <Button size="sm" colorPalette="brand" loading={saving} loadingText={t('common.saving')} disabled={!ws.draft.dirty} onClick={handleSave}>
                  {t('common.save')}
                </Button>
              </>
            ) : (
              <Button size="sm" colorPalette="brand" disabled={blocked} onClick={() => ws.startEdit(section.id)}>
                <LuPencilLine /> {t('common.edit')}
              </Button>
            )}
          </Flex>
        )}
      </Flex>

      <Box
        data-testid="settings-tab-content"
        bg="var(--mc-bg-surface)"
        border="1px solid"
        borderColor={editing ? 'var(--mc-border-strong)' : 'var(--mc-border)'}
        borderRadius="lg"
        p={{ base: 4, md: 6 }}
      >
        <Component s={s} />
      </Box>
    </Stack>
  )
}
